import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { GoHome, GoHomeFill } from 'react-icons/go';
import { HiOutlineBookOpen, HiBookOpen } from 'react-icons/hi2';
import { BsCalendar3, BsCalendar3Fill } from 'react-icons/bs';
import { BiMessageRounded, BiSolidMessageRounded } from 'react-icons/bi';
import { IoChatbubbleEllipsesOutline, IoChatbubbleEllipses } from 'react-icons/io5';
import { HiOutlineUser, HiUser } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';

const NAV_ITEMS = [
  { path: '/home', label: '홈', icon: GoHome, activeIcon: GoHomeFill },
  { path: '/class', label: '클래스', icon: HiOutlineBookOpen, activeIcon: HiBookOpen },
  { path: '/calendar', label: '캘린더', icon: BsCalendar3, activeIcon: BsCalendar3Fill },
  { path: '/chat', label: '채팅', icon: IoChatbubbleEllipsesOutline, activeIcon: IoChatbubbleEllipses },
  { path: '/message', label: '메시지', icon: BiMessageRounded, activeIcon: BiSolidMessageRounded },
  { path: '/profile', label: '프로필', icon: HiOutlineUser, activeIcon: HiUser },
];

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);

  // 관리자 메시지 안읽음
  useEffect(() => {
    if (!userData?.uid) return;
    const loadUnread = async () => {
      try {
        const uid = userData.uid;
        const role = userData?.role || '';
        const snap = await getDocs(collection(db, 'adminMessages'));
        let count = 0;
        snap.docs.forEach(d => {
          const msg = d.data();
          const isTarget = msg.targetUserIds?.includes(uid) || msg.targetRole === 'all' || msg.targetRole === role;
          if (isTarget && !(msg.readBy || {})[uid]) count++;
        });
        setUnreadCount(count);
      } catch {}
    };
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    const onRead = () => loadUnread();
    window.addEventListener('message-read', onRead);
    return () => { clearInterval(interval); window.removeEventListener('message-read', onRead); };
  }, [userData?.uid, userData?.role, location.pathname]);

  // 채팅 안읽음 (실시간)
  useEffect(() => {
    if (!userData?.uid) return;
    const q = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', userData.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      let total = 0;
      snap.docs.forEach(d => {
        total += (d.data().unreadCount?.[userData.uid] || 0);
      });
      setChatUnread(total);
    }, () => {});
    return () => unsub();
  }, [userData?.uid]);

  return (
    <NavContainer>
      {NAV_ITEMS.map((item) => {
        const isActive = location.pathname.startsWith(item.path);
        const Icon = isActive ? item.activeIcon : item.icon;
        const isStudentRole = userData?.role === 'student';
        const isParentRole = userData?.role === 'parent';
        return (
          <NavItem key={item.path} onClick={() => navigate(item.path)} $active={isActive} $student={isStudentRole} $parent={isParentRole}>
            <IconWrap>
              <Icon size={24} />
              {item.path === '/message' && unreadCount > 0 && (
                <UnreadBadge>{unreadCount > 99 ? '99+' : unreadCount}</UnreadBadge>
              )}
              {item.path === '/chat' && chatUnread > 0 && (
                <UnreadBadge>{chatUnread > 99 ? '99+' : chatUnread}</UnreadBadge>
              )}
            </IconWrap>
            <NavLabel>{item.label}</NavLabel>
          </NavItem>
        );
      })}
    </NavContainer>
  );
};

export default BottomNav;

const isRnWebView = !!window.ReactNativeWebView;

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: ${isRnWebView ? '84px' : '64px'};
  background: #FFFFFF;
  border-top: 1px solid #E5E7EB;
  display: flex;
  align-items: flex-start;
  padding-top: 10px;
  justify-content: space-around;
  z-index: 100;
  padding-bottom: ${isRnWebView ? '20px' : 'env(safe-area-inset-bottom)'};
`;

const NavItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: ${({ $active, $student, $parent, theme }) => $active ? ($parent ? theme.colors.parentPrimary : $student ? theme.colors.studentPrimary : theme.colors.primary) : theme.colors.text.hint};
  transition: color 0.2s;
`;

const IconWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-primary);
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
`;
